import React from 'react';
import classNames from 'classnames';
import { SortHeaderCellProps, SortItem } from 'ali-react-table';
import SvgIcon from '@home/components/SvgIcon';

// 自定义排序表头
const sortCfg = {
  desc: 'arrow-big-down',
  asc: 'arrow-big-up',
  none: 'sort-icon',
};

interface IColumnWithDescKey {
  descKey?: string;
}

const DEFAULT_SORT_ORDERS: string[] = ['desc', 'asc', 'none'];

const MultipleSortHeaderCell = (props: SortHeaderCellProps): React.ReactElement => {
  const { sortOptions, children, sortOrder, sortIndex, column } = props;
  const { descKey } = (column as IColumnWithDescKey) ?? {};
  const { onChangeSorts, sorts, orders } = sortOptions || {};
  const safeSorts: SortItem[] = Array.isArray(sorts) ? sorts : [];
  const safeOrders = Array.isArray(orders) && orders.length > 0 ? orders : DEFAULT_SORT_ORDERS;
  const number = sortIndex > -1 && safeSorts.length > 1 ? sortIndex + 1 : '';
  
  // 报表编辑页面 不需要展示排序按钮, 此时如果不使用MultipleSortHeaderCell 渲染表头, 将无法实现 超出两行显示省略号, 显示字段描述等功能.
  // 但ali表格的表头目前只找到通过排序表头设置可以更改 表头的渲染
  // 所以在报表编辑页面时, 不展示排序按钮, column设置 features.sortable 为 true, 让表格可以使用当前组件渲染
  // 展示按钮时column设置 features.sortable 为对应的 排序方法.
  const isShowSort = typeof column.features?.sortable !== 'boolean';

  /**
   * 排序切换交互约定：
   * - 仅在可排序模式下生效；
   * - 轮转顺序维持既有实现：desc -> asc -> none；
   * - 无排序时从排序数组中移除当前列，保持多列排序相对顺序不变。
   */
  const handleSortClick = (): void => {
    if (!isShowSort || safeOrders.length === 0) {
      return;
    }

    // TODO(datalab): 这部分逻辑与 orders ['desc', 'asc', 'none'] 强关联，后续可抽离状态机统一管理。
    const orderIndex = safeOrders.findIndex((order) => order === sortOrder);
    const nextOrderIndex = (orderIndex + 1) % 3;
    const nextOrder = safeOrders[nextOrderIndex] || safeOrders[0];
    let newSorts: SortItem[] = [];

    if (nextOrder === 'none') {
      newSorts = safeSorts.filter((_, index) => index !== sortIndex);
    } else {
      const nextSort: SortItem = {
        code: column.code as string,
        order: nextOrder as SortItem['order'],
      };
      newSorts = [...safeSorts];

      if (sortIndex < 0) {
        newSorts.push(nextSort);
      } else {
        newSorts[sortIndex] = nextSort;
      }
    }

    onChangeSorts?.(newSorts);
  };
  
  return (<div
    className={classNames('dbl-multiple-sort-header-cell', { 'is-not-sort': !isShowSort })}
    onClick={isShowSort ? handleSortClick : undefined}
    style={{ justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : ''}}
  >
    <span
      className='dbl-multiple-sort-header-cell-text'
      data-description-key={descKey}
    >
      {children}
    </span>
    {isShowSort ? (
      <span
        className={classNames('dbl-multiple-sort-header-cell-sort', { 'is-active': sortOrder !== 'none' })}
        data-sort-number={number}
        data-order={sortOrder}
        data-sort-count={safeSorts.length}
      >
        <SvgIcon type={(sortCfg[sortOrder] as string) || 'sort-icon'} />
        <span className={'dbl-multiple-sort-header-cell-sort-number'}>{number}</span>
      </span>) : ''
    }
  </div>);
};

export default MultipleSortHeaderCell;